import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Check, Globe, ScanSearch } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '#/components/ui/button'
import { Checkbox } from '#/components/ui/checkbox'
import { Input } from '#/components/ui/input'
import { Label } from '#/components/ui/label'
import { Textarea } from '#/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '#/components/ui/select'
import { WaitStatus } from '#/components/common/loading-state'
import { ModelPicker } from '#/components/common/model-picker'
import { ApiError } from '#/lib/api'
import { createSitePlan } from '#/lib/api-site'
import { useHydratedSession } from '#/lib/auth-client'
import { useBuilderMode } from '#/lib/simple-mode'
import type { ModelId } from '#/lib/ai/models'
import { domainPolicy, isSensitivePath } from '#/lib/site/policy'
import { normalizeSiteUrl, siteUrlError } from '#/lib/site/url'
import { SITE_NOTES_MAX, SITE_RELATIONS, type SiteRelation } from '#/lib/site/types'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'
import { getLocale } from '#/paraglide/runtime'

/** Localised text for URL problems found in the browser or returned by POST /api/plan/site. */
export function siteUrlErrorText(code: string | null | undefined, fallback?: string): string {
  const t: Record<string, () => string> = {
    empty: m.site_url_error_empty, too_long: m.site_url_error_too_long, invalid_url: m.site_url_error_invalid_url,
    unsupported_scheme: m.site_url_error_unsupported_scheme, credentials_in_url: m.site_url_error_credentials_in_url,
    port_not_allowed: m.site_url_error_port_not_allowed, ip_not_allowed: m.site_url_error_ip_not_allowed,
    host_not_public: m.site_url_error_host_not_public, address_not_public: m.site_url_error_address_not_public,
    dns_failed: m.site_url_error_dns_failed, domain_denied: m.site_url_error_domain_denied, domain_finance: m.site_url_error_domain_finance,
    sensitive_page: m.site_url_error_sensitive_page, consent_required: m.site_url_error_consent_required,
    site_daily_limit: m.site_url_error_site_daily_limit, site_busy: m.site_url_error_site_busy, site_locked: m.site_url_error_site_locked,
    site_disabled: m.site_url_error_site_disabled,
  }
  return (code && t[code]?.()) || fallback || m.site_url_error_invalid_url()
}

/** Same rules the server applies before any DNS lookup (url.ts + policy.ts). */
export function localUrlProblem(input: string): string | null {
  const code = siteUrlError(input)
  if (code) return code
  const n = normalizeSiteUrl(input)
  const policy = domainPolicy(n.host)
  if (policy.kind === 'deny') return policy.reason === 'finance' ? 'domain_finance' : 'domain_denied'
  if (isSensitivePath(new URL(n.href).pathname)) return 'sensitive_page'
  return null
}

const RELATION_TEXT: Record<SiteRelation, { title: () => string; desc: () => string }> = {
  inspiration: { title: m.site_relation_inspiration, desc: m.site_relation_inspiration_desc },
  competitor: { title: m.site_relation_competitor, desc: m.site_relation_competitor_desc },
  own: { title: m.site_relation_own, desc: m.site_relation_own_desc },
  client: { title: m.site_relation_client, desc: m.site_relation_client_desc },
}
const URL_CODES = new Set(['empty', 'too_long', 'invalid_url', 'unsupported_scheme', 'credentials_in_url', 'port_not_allowed', 'ip_not_allowed', 'host_not_public', 'address_not_public', 'dns_failed', 'domain_denied', 'domain_finance', 'sensitive_page'])

/** /plan?flow=website — the entry of website reverse engineering (docs/reverse-engineering/PLAN.md). */
export function SiteForm({ workspaceId }: { workspaceId?: string }) {
  const navigate = useNavigate()
  const { data: session } = useHydratedSession()
  const { simple } = useBuilderMode()
  const [url, setUrl] = useState('')
  const [touched, setTouched] = useState(false)
  const [notes, setNotes] = useState('')
  const [relation, setRelation] = useState<SiteRelation>('inspiration')
  const [language, setLanguage] = useState<'en' | 'id'>(getLocale() === 'id' ? 'id' : 'en')
  const [model, setModel] = useState<ModelId | null>(null)
  const [consent, setConsent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const problem = useMemo(() => (url.trim() ? localUrlProblem(url) : 'empty'), [url])
  const shownError = serverError ?? (touched && url.trim() && problem ? problem : null)
  const ready = !problem && consent && !busy

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setTouched(true)
    if (!ready) return
    if (!session?.user) {
      navigate({ to: '/sign-in', search: { redirect: '/plan?flow=website' } })
      return
    }
    setBusy(true)
    setServerError(null)
    try {
      const r = await createSitePlan({ url: url.trim(), notes: notes.trim() || undefined, relation, language, model: simple ? null : model, ...(simple ? { summaryMode: 'simple' as const } : {}), ...(workspaceId ? { workspaceId } : {}), consent: true })
      navigate({ to: '/plans/$planId', params: { planId: r.planId }, search: { step: 1 } })
    } catch (err) {
      setBusy(false)
      if (err instanceof ApiError && err.code && URL_CODES.has(err.code)) setServerError(err.code)
      else if (err instanceof ApiError && err.code) toast.error(siteUrlErrorText(err.code))
      else toast.error(err instanceof ApiError ? err.message : m.common_error_generic())
    }
  }

  return (
    <form onSubmit={submit} className="mx-auto w-full max-w-2xl space-y-6" data-testid="site-form" noValidate>
      <WaitStatus active={busy} label={m.site_submitting()} description={m.site_time_hint()} />
      <div className="space-y-2">
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
          <ScanSearch className="size-4" /> {m.site_choose_title()}
        </p>
        <h1 className="text-3xl font-bold sm:text-4xl">{m.site_form_title()}</h1>
        <p className="text-muted-foreground">{m.site_form_subtitle()}</p>
      </div>

      <div className="space-y-4 rounded-2xl border border-primary/50 bg-card p-4 shadow-sm focus-within:border-primary sm:p-5">
        <div className="space-y-1.5">
          <Label htmlFor="site-url">{m.site_url_label()}</Label>
          <div className="relative">
            <Globe className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="site-url"
              data-testid="site-url"
              value={url}
              onChange={(e) => { setUrl(e.target.value); setServerError(null) }}
              onBlur={() => setTouched(true)}
              placeholder={m.site_url_placeholder()}
              inputMode="url"
              autoComplete="url"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={2048}
              aria-invalid={Boolean(shownError)}
              aria-describedby={shownError ? 'site-url-error' : undefined}
              className="h-11 pl-9 text-base"
              disabled={busy}
            />
          </div>
          {shownError ? <p id="site-url-error" data-testid="site-url-error" role="alert" className="text-xs text-destructive">{siteUrlErrorText(shownError)}</p> : null}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="site-notes">{m.site_notes_label()}</Label>
          <Textarea id="site-notes" data-testid="site-notes" value={notes} onChange={(e) => setNotes(e.target.value.slice(0, SITE_NOTES_MAX))} rows={3} placeholder={m.site_notes_placeholder()} disabled={busy} />
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className="mb-2 text-sm font-medium">{m.site_relation_label()}</legend>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {SITE_RELATIONS.map((r) => (
            <button
              key={r}
              type="button"
              data-testid={`site-relation-${r}`}
              aria-pressed={relation === r}
              onClick={() => setRelation(r)}
              disabled={busy}
              className={cn('relative rounded-xl border bg-card px-3 py-2.5 text-left transition-colors hover:border-primary/60', relation === r ? 'border-primary bg-primary/10 ring-1 ring-primary' : 'border-border')}
            >
              {relation === r ? <Check className="absolute right-2 top-2 size-3.5 text-primary" aria-hidden /> : null}
              <span className="block pr-4 text-sm font-medium">{RELATION_TEXT[r].title()}</span>
              <span className="block text-xs text-muted-foreground">{RELATION_TEXT[r].desc()}</span>
            </button>
          ))}
        </div>
      </fieldset>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={language} onValueChange={(v) => setLanguage(v as 'en' | 'id')} disabled={busy}>
          <SelectTrigger size="sm" className="h-9 gap-1.5 text-xs" aria-label={m.site_language_label()}>
            <Globe className="size-4" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="id">{m.idea_language_id()}</SelectItem>
            <SelectItem value="en">{m.idea_language_en()}</SelectItem>
          </SelectContent>
        </Select>
        {simple ? null : <ModelPicker value={model} onChange={setModel} allowDefault className="h-9 text-xs" />}
      </div>

      <div className="space-y-2 rounded-xl border bg-muted/30 p-4">
        <label className="flex cursor-pointer items-start gap-3" htmlFor="site-consent">
          <Checkbox id="site-consent" data-testid="site-consent" checked={consent} onCheckedChange={(v) => setConsent(v === true)} disabled={busy} className="mt-0.5" />
          <span className="text-sm leading-relaxed">{m.site_consent()}</span>
        </label>
        <p className="pl-7 text-xs text-muted-foreground">
          {m.site_disclaimer()}{' '}
          <a href="/bot" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-foreground">{m.site_bot_link()}</a>
        </p>
        {touched && !consent && !problem ? <p className="pl-7 text-xs text-destructive" role="alert">{m.site_url_error_consent_required()}</p> : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">{m.site_time_hint()}</span>
        <Button type="submit" data-testid="site-submit" loading={busy} disabled={!ready} className="h-11 px-6">
          <ScanSearch className="size-4" /> {m.site_submit()}
        </Button>
      </div>
    </form>
  )
}
